import React, { useState } from 'react';
import { Heading } from '../../components';
import { NamedLink, AvatarLarge, AvatarMedium } from '../../components';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import css from './ListingPage.module.css';

const mapCareStyle = (petCareStyle) => {
  switch (petCareStyle) {
    case 'positive_reinforcement':
      return (<img src="/static/images/carestyle_positive_reinforcement.jpg" alt="Positive Reinforcement" title="Positive Reinforcement" style={{ maxWidth: 300, height: 'auto', verticalAlign: 'middle', marginRight: 5 }} />);
      return 'Positive Reinforcement';
    case 'positive_punishment':
      return (<img src="/static/images/carestyle_positive_punishment.jpg" alt="Positive Punishment" title="Positive Punishment" style={{ maxWidth: 300, height: 'auto', verticalAlign: 'middle', marginRight: 5 }} />);
      return 'Positive Punishment';
    case 'negative_reinforcement':
      return (<img src="/static/images/carestyle_negative_reinforcement.jpg" alt="Negative Reinforcement" title="Negative Reinforcement" style={{ maxWidth: 300, height: 'auto', verticalAlign: 'middle', marginRight: 5 }} />);
      return 'Negative Reinforcement';
    case 'negative_punishment':
      return (<img src="/static/images/carestyle_negative_punishment.jpg" alt="Negative Punishment" title="Negative Punishment" style={{ maxWidth: 300, height: 'auto', verticalAlign: 'middle', marginRight: 5 }} />);
      return 'Negative Punishment';
    default:
      return petCareStyle;
  }
};

const mapMedicalExperience = (key) => {
  switch (key) {
    case 'pills':
      return 'medicationPills';
    case 'topical':
      return 'medicationTopical';
    case 'eyeDrops':
      return 'medicationEyeDrops';
    case 'injections':
      return 'medicationInjections';
    default:
      return key;
  }
};

const mapMedicalNeeds = (key) => {
  switch (key) {
    case 'stressInducedColitis':
      return 'stressColitis';
    default:
      return key;
  }
};

const mapBehaviors = (key) => {
  switch (key) {
    case 'otherDogs':
      return 'animalReactivity';
    default:
      return key;
  }
}

const hasAnyExperience = obj =>
  Object.values(obj || {}).some(level => level > 0);

const SectionProfile = props => {
  const { listing, intl } = props;
  // console.log('SectionProfile', listing);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? `info-popover` : undefined;

  const profileData = listing?.attributes?.profileData || {};
  const { yearsExperience, careStyle, certifications, medicationExperience, medicalExperience, behaviorExperience } = profileData;

  const hasMedicationExperience = hasAnyExperience(medicationExperience);
  const hasMedicalExperience = hasAnyExperience(medicalExperience);
  const hasBehaviorExperience = hasAnyExperience(behaviorExperience);

  return (
    <div className={css.sectionProfile}>
      {
        profileData?.about
        && (
          <section className={css.sectionText}>
            <Heading as="h2" rootClassName={css.sectionHeading}>
              About the Pet Sitter
            </Heading>
            <p className={css.text}>
              {profileData.about || 'No description available.'}
            </p>
          </section>
        )
      }
      {
        yearsExperience
        && yearsExperience.jobType
        && yearsExperience.years
        && (
          <section className={css.sectionText}>
            <Heading as="h2" rootClassName={css.sectionHeading}>
              Years of Experience
            </Heading>
            <p className={css.text}>
              <span style={{ marginRight: 5 }}>
                {yearsExperience.jobType && intl.formatMessage({ id: `ProfileExtendedSettingsPage.experienceType.${yearsExperience.jobType}` })}:
              </span>
              {`${yearsExperience.years} years`}
            </p>
          </section>
        )
      }
      {
        careStyle?.careStyle && (
          <section className={css.sectionText}>
            <Heading as="h2" rootClassName={css.sectionHeading}>
              Care Style
            </Heading>
            <p className={css.text}>
              {Array.isArray(careStyle.careStyle)
                ? careStyle.careStyle.map(mi => mapCareStyle(mi))
                : mapCareStyle(careStyle.careStyle)}
            </p>
          </section>
        )
      }
      {
        Array.isArray(certifications) && certifications.length > 0 && (
          <section className={css.sectionText}>
            <Heading as="h2" rootClassName={css.sectionHeading}>
              Certifications
            </Heading>
            <ul className={css.certificationsList}>
              {certifications.map(cert => (
                <li key={cert} className={css.certificationItem}>
                  {intl.formatMessage({ id: `ProfileExtendedSettingsPage.certification.${cert}` })}
                </li>
              ))}
            </ul>
          </section>
        )
      }
      {
        hasMedicationExperience && (
          <section className={css.sectionText}>
            <div className={css.headerWithInfo}>
              <Heading as="h2" rootClassName={css.sectionHeading}>
                Medication Experience
              </Heading>
              <IconButton aria-describedby={id} variant="contained" onClick={handleClick} size="small">
                <InfoOutlinedIcon />
              </IconButton>
            </div>
            <ul className={css.certificationsList}>
              {Object.keys(medicationExperience).filter(key => medicationExperience[key] > 0).map(key => {
                const level = medicationExperience[key];
                const level_array = [];
                for (let i = 0; i < level; i++) {
                  level_array.push('★');
                }
                return (
                  <li key={key} className={css.certificationItem}>
                    <span style={{ marginRight: 5 }}>
                      {intl.formatMessage({ id: `ProfileExtendedSettingsPage.medicalExperience.${mapMedicalExperience(key)}` })}:
                    </span>
                    {
                      level_array.map((star, starIndex) => (
                        <span key={starIndex} className={css.star}>
                          {star}
                        </span>
                      ))
                    }
                  </li>
                );
              })}
              {
                medicationExperience.other && (
                  <li className={css.certificationItem}>
                    <span style={{ marginRight: 5 }}>Other:</span>
                    {medicationExperience.other}
                  </li>
                )
              }
            </ul>
          </section>
        )
      }

      {
        hasMedicalExperience && (
          <section className={css.sectionText}>
            <div className={css.headerWithInfo}>
              <Heading as="h2" rootClassName={css.sectionHeading}>
                Medical Experience
              </Heading>
              <IconButton aria-describedby={id} variant="contained" onClick={handleClick} size="small">
                <InfoOutlinedIcon />
              </IconButton>
            </div>
            <ul className={css.certificationsList}>
              {Object.keys(medicalExperience).filter(key => medicalExperience[key] > 0).map(key => {
                const level = medicalExperience[key];
                const level_array = [];
                for (let i = 0; i < level; i++) {
                  level_array.push('★');
                }
                return (
                  <li key={key} className={css.certificationItem}>
                    <span style={{ marginRight: 5 }}>
                      {intl.formatMessage({ id: `ProfileExtendedSettingsPage.medicalNeeds.${mapMedicalNeeds(key)}` })}:
                    </span>
                    {
                      level_array.map((star, starIndex) => (
                        <span key={starIndex} className={css.star}>
                          {star}
                        </span>
                      ))
                    }
                  </li>
                );
              })}
              {
                medicalExperience.other && (
                  <li className={css.certificationItem}>
                    <span style={{ marginRight: 5 }}>Other:</span>
                    {medicalExperience.other}
                  </li>
                )
              }
            </ul>
          </section>
        )
      }
      
      {
        hasBehaviorExperience && (
          <section className={css.sectionText}>
            <div className={css.headerWithInfo}>
              <Heading as="h2" rootClassName={css.sectionHeading}>
                Behavior Experience
              </Heading>
              <IconButton aria-describedby={id} variant="contained" onClick={handleClick} size="small">
                <InfoOutlinedIcon />
              </IconButton>
            </div>
            <ul className={css.certificationsList}>
              {Object.keys(behaviorExperience).filter(key => behaviorExperience[key] > 0).map(key => {
                const level = behaviorExperience[key];
                const level_array = [];
                for (let i = 0; i < level; i++) {
                  level_array.push('★');
                }
                return (
                  <li key={key} className={css.certificationItem}>
                    <span style={{ marginRight: 5 }}>
                      {intl.formatMessage({ id: `ProfileExtendedSettingsPage.petBehaviors.${mapBehaviors(key)}` })}:
                    </span>
                    {
                      level_array.map((star, starIndex) => (
                        <span key={starIndex} className={css.star}>
                          {star}
                        </span>
                      ))
                    }
                  </li>
                );
              })}
              {
                behaviorExperience.other && (
                  <li className={css.certificationItem}>
                    <span style={{ marginRight: 5 }}>Other:</span>
                    {behaviorExperience.other}
                  </li>
                )
              }
            </ul>
          </section>
        )
      }

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div style={{ padding: '16px' }}>
          <p>
            ★ - a little experience
          </p>
          <p>
            ★★ - some experience
          </p>
          <p>
            ★★★ - a lot of experience
          </p>
          <p>
            ★★★★ - expert
          </p>
        </div>
      </Popover>
    </div>
  );
};

export default SectionProfile;
